import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FiSearch, FiEdit2, FiTrash2, FiX, FiEye } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { UserDefinedTemplate } from '../../features/templates/types';
import {
  removeUserDefinedTemplate,
  setCurrentTemplateId,
  setUserDefinedCategoryCounts,
} from '../../features/templates/templatesSlice';
import { useNavigate } from 'react-router-dom';
import { deleteUserTemplate } from '../../features/templates/templateService';
import CreateTemplateModal from './CreateTemplateModal';
import ViewTemplateModal from './ViewTemplateModal';
import { AppDispatch, RootState } from '../../app/store';
import fetchUserDefinedTemplates from '../../features/templates/userDefinedTemplatesApi';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

const FiSearchComponent = FiSearch as any;
const FiEdit2Component = FiEdit2 as any;
const FiTrash2Component = FiTrash2 as any;
const FiXComponent = FiX as any;
const FiEyeComponent = FiEye as any;

const UNCATEGORIZED = 'Uncategorized';

interface YourTemplatesProps {
  templates: UserDefinedTemplate[];
  onSelect: (id: string) => void;
  fromTab2: string | undefined;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
}

const getTemplateCategory = (template: any, allTemplates: any[]): string => {
  const fullTemplate = allTemplates.find((t) => t.id === template.id);
  const category =
    fullTemplate?.metadata?.category || template.metadata?.category;
  return category?.trim() ? category.trim() : UNCATEGORIZED;
};

const YourTemplates: React.FC<YourTemplatesProps> = ({
  templates,
  onSelect,
  fromTab2,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onSearch,
  searchTerm,
}) => {
  const [searchQuery, setSearchQuery] = useState(searchTerm || '');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewTemplateName, setViewTemplateName] = useState('');
  const [viewTemplateId, setViewTemplateId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Tooltip state (portal-rendered)
  const [tooltip, setTooltip] = useState<{
    text: string;
    top: number;
    left: number;
  } | null>(null);

  // All templates fetched with size=1000
  const [allTemplates, setAllTemplates] = useState<UserDefinedTemplate[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);

  const [localPage, setLocalPage] = useState<number>(1);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const userDefinedCategoryCounts = useSelector(
    (state: RootState) => state.templates.userDefinedCategoryCounts
  );
  const userDefinedStatus = useSelector(
    (state: RootState) => state.templates.userDefinedStatus
  );

  // ====================================================================
  // Fetch ALL templates
  // ====================================================================
  const fetchAllCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      const { token, tenantId, userId } = getAuthContext();
      if (!token) return;

      let url = `${BASE_URL}/v1/templates/user-define?orgId=org_123456&page=0&size=1000`;
      if (searchTerm && searchTerm.trim()) {
        url += `&name=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId || '',
          'X-User-Id': userId || '',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // console.error('Failed to fetch all templates:', response.status);
        toast.error('Failed to fetch templates. Please try again later.');
        setAllTemplates([]);
        setAllCategories([]);
        dispatch(setUserDefinedCategoryCounts({}));
        return;
      }

      const data = await response.json();
      const content: any[] = data.content || [];

      const active = content.filter((t) => t.status !== 'ARCHIVED');

      const counts: Record<string, number> = {};
      const set = new Set<string>();

      active.forEach((t) => {
        const cat = t.metadata?.category?.trim()
          ? t.metadata.category.trim()
          : UNCATEGORIZED;
        set.add(cat);
        counts[cat] = (counts[cat] || 0) + 1;
      });

      const sortedCategories = Array.from(set).sort((a, b) => {
        if (a === UNCATEGORIZED) return 1;
        if (b === UNCATEGORIZED) return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      });

      setAllTemplates(active as UserDefinedTemplate[]);
      setAllCategories(sortedCategories);
      dispatch(setUserDefinedCategoryCounts(counts));
    } catch (err) {
      // console.error('Error fetching all templates:', err);
      toast.error('Error fetching templates. Please try again later.');
      setAllTemplates([]);
      setAllCategories([]);
      dispatch(setUserDefinedCategoryCounts({}));
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== searchTerm) {
        onSearch(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, onSearch, searchTerm]);

  useEffect(() => {
    if (searchTerm !== searchQuery) {
      setSearchQuery(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (!tooltip) return;
    const hide = () => setTooltip(null);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [tooltip]);

  const isCategoryFilterActive = selectedCategories.length > 0;
  const ITEMS_PER_PAGE = pageSize || 9;

  const filteredByCategory = useMemo(() => {
    if (!isCategoryFilterActive) return [];
    return allTemplates.filter((t) =>
      selectedCategories.includes(getTemplateCategory(t, allTemplates))
    );
  }, [allTemplates, selectedCategories, isCategoryFilterActive]);

  useEffect(() => {
    setLocalPage(1);
  }, [selectedCategories]);

  const paginatedFiltered = useMemo(() => {
    const startIndex = (localPage - 1) * ITEMS_PER_PAGE;
    return filteredByCategory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredByCategory, localPage, ITEMS_PER_PAGE]);

  const displayTotalPages = isCategoryFilterActive
    ? Math.ceil(filteredByCategory.length / ITEMS_PER_PAGE)
    : totalPages;

  const displayCurrentPage = isCategoryFilterActive ? localPage : currentPage + 1;

  const displayTotalCount = isCategoryFilterActive
    ? filteredByCategory.length
    : totalCount;

  const displayedTemplates = isCategoryFilterActive
    ? paginatedFiltered
    : templates;

  const startItem = isCategoryFilterActive
    ? filteredByCategory.length === 0
      ? 0
      : (localPage - 1) * ITEMS_PER_PAGE + 1
    : totalCount === 0
      ? 0
      : currentPage * pageSize + 1;

  const endItem = isCategoryFilterActive
    ? Math.min(localPage * ITEMS_PER_PAGE, filteredByCategory.length)
    : Math.min((currentPage + 1) * pageSize, totalCount);

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    onSearch('');
  };

  const handlePageChange = (page: number) => {
    const apiPage = page - 1;
    onPageChange(apiPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLocalPageChange = (page: number) => {
    if (page >= 1 && page <= displayTotalPages) {
      setLocalPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const openDeleteConfirm = (id: string, name: string) => {
    setTemplateToDelete({ id, name });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    setDeletingId(templateToDelete.id);
    setDeleteConfirmOpen(false);

    try {
      await deleteUserTemplate(templateToDelete.id, templateToDelete.name);
      dispatch(removeUserDefinedTemplate(templateToDelete.id));

      setAllTemplates((prev) =>
        prev.filter((t) => t.id !== templateToDelete.id)
      );

      if (searchTerm) {
        dispatch(
          fetchUserDefinedTemplates({
            orgId: 'org_123456',
            name: searchTerm,
          })
        );
      } else {
        dispatch(
          fetchUserDefinedTemplates({
            orgId: 'org_123456',
            page: currentPage,
            size: pageSize,
          })
        );
      }
      fetchAllCategories();
    } catch (error) {
      // console.error('Delete failed:', error);
      toast.error('Failed to delete template');
    } finally {
      setDeletingId(null);
      setTemplateToDelete(null);
    }
  };

  // ====================================================================
  // EDIT handler (only DRAFT templates)
  // ====================================================================
  const handleEditClick = (
    e: React.MouseEvent,
    template: UserDefinedTemplate
  ) => {
    e.stopPropagation();
    if (template.status === 'DRAFT') {
      localStorage.setItem('currentTemplateId', template.id);
      dispatch(setCurrentTemplateId(template.id));
      navigate('/templates/editor', {
        state: { fromTab2: fromTab2 },
      });
    } else {
      toast.info('Only draft templates can be edited', { autoClose: 3000 });
    }
  };

  // ====================================================================
  // VIEW handler (fetches template HTML and opens modal)
  // Same logic as handlePreviewEmail in DetailPanel
  // ====================================================================
  const handleViewClick = async (
    e: React.MouseEvent,
    template: UserDefinedTemplate
  ) => {
    e.stopPropagation();

    setViewTemplateId(template.id);
    setViewTemplateName(template.name);
    setPreviewHtml('');
    setViewModalOpen(true);
    setPreviewLoading(true);

    const { token, tenantId, userId } = getAuthContext();
    if (!token || !tenantId) {
      toast.error('Authentication required');
      setPreviewLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/v1/templates/user-define/${template.id}`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const templateData = await response.json();

      let htmlContent = '';

      if (templateData.html) {
        htmlContent = templateData.html;
      } else if (
        templateData.blocks &&
        Array.isArray(templateData.blocks)
      ) {
        const rawHtmlBlocks = templateData.blocks.filter(
          (block: any) => block.type === 'rawHtml'
        );
        htmlContent = rawHtmlBlocks
          .map((block: any) => block.text)
          .join('\n');

        if (!htmlContent) {
          const paragraphBlocks = templateData.blocks.filter(
            (block: any) => block.type === 'paragraph'
          );
          htmlContent = paragraphBlocks
            .map((block: any) => block.text)
            .join('\n');
        }
      } else if (templateData.content) {
        htmlContent = templateData.content;
      }

      if (templateData.styles && htmlContent) {
        const hasStyleTag = /<style[^>]*>/.test(htmlContent);

        if (hasStyleTag) {
          htmlContent = htmlContent.replace(
            /<style[^>]*>([\s\S]*?)<\/style>/,
            `<style>${templateData.styles}</style>`
          );
        } else {
          if (/<head[^>]*>/.test(htmlContent)) {
            htmlContent = htmlContent.replace(
              /<head[^>]*>/,
              `<head><style>${templateData.styles}</style>`
            );
          } else {
            htmlContent = `<style>${templateData.styles}</style>\n${htmlContent}`;
          }
        }
      }

      if (!htmlContent) {
        toast.warning(
          'No content available for preview. The template might be empty.'
        );
      }

      setPreviewHtml(htmlContent);
    } catch (error: any) {
      // console.error('❌ Failed to fetch template preview:', error);
      toast.error(error.message || 'Failed to load template preview');
      setPreviewHtml('');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setPreviewHtml('');
    setViewTemplateId(null);
    setViewTemplateName('');
  };

  const handleEditFromViewModal = () => {
    if (!viewTemplateId) return;
    const template = templates.find((t) => t.id === viewTemplateId)
      || allTemplates.find((t) => t.id === viewTemplateId);

    if (!template) return;

    if (template.status === 'DRAFT') {
      localStorage.setItem('currentTemplateId', template.id);
      dispatch(setCurrentTemplateId(template.id));
      closeViewModal();
      navigate('/templates/editor', {
        state: { fromTab2: fromTab2 },
      });
    }
  };

  const handleNameMouseEnter = (
    e: React.MouseEvent<HTMLHeadingElement>,
    text: string
  ) => {
    const el = e.currentTarget;
    if (el.scrollWidth <= el.clientWidth) return;

    const rect = el.getBoundingClientRect();
    setTooltip({
      text,
      top: rect.top + window.scrollY - 8,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  };

  const handleNameMouseLeave = () => setTooltip(null);

  if (userDefinedStatus === 'loading' && !isCategoryFilterActive) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className='flex gap-6'>
        {/* SIDEBAR */}
        <div
          className='w-1/5 bg-white rounded-md p-5 border border-gray-200 shadow-sm sticky top-6'
          style={{ height: 'fit-content' }}
        >
          <button
            className='w-full text-left font-medium mb-4 px-3 py-2 rounded flex justify-between items-center'
            style={{ backgroundColor: '#E0F8FB', color: '#00899E' }}
            onClick={() => {
              setSelectedCategories([]);
              setLocalPage(1);
              onPageChange(0);
            }}
          >
            All Templates
            <span className='bg-blue-600 text-white text-xs px-2 py-1 rounded'>
              {totalCount}
            </span>
          </button>

          <div className='space-y-2'>
            {isCategoriesLoading && allCategories.length === 0 && (
              <div className='text-xs text-gray-400 px-2 py-1'>
                Loading categories…
              </div>
            )}

            {!isCategoriesLoading && allCategories.length === 0 && (
              <div className='text-xs text-gray-400 px-2 py-1'>
                No categories
              </div>
            )}

            {allCategories.map((cat) => {
              const count = userDefinedCategoryCounts[cat] || 0;

              return (
                <label
                  key={cat}
                  className='flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer'
                >
                  <input
                    type='checkbox'
                    checked={selectedCategories.includes(cat)}
                    onChange={() => handleCategoryChange(cat)}
                    className='w-4 h-4 text-blue-600 rounded focus:ring-blue-500'
                  />
                  <span className='text-sm text-gray-700 flex-1'>{cat}</span>
                  <span className='text-xs text-gray-500'>({count})</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* MAIN */}
        <div className='flex-1'>
          <div className='flex items-center justify-between mb-6'>
            <div className='relative flex-1 w-full'>
              <input
                type='text'
                placeholder='Search your templates...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pl-4 pr-14 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
              {searchQuery && (
                <button
                  type='button'
                  onClick={handleClearSearch}
                  className='absolute right-9 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10'
                  aria-label='Clear search'
                >
                  <svg
                    className='w-5 h-5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              )}
              <FiSearchComponent
                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'
                size={20}
              />
            </div>

            <button
              onClick={() => setShowModal(true)}
              className='ml-4 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-sm transition'
            >
              + Create Template
            </button>
          </div>

          {displayedTemplates.length > 0 ? (
            <>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {displayedTemplates.map((template) => {
                  const isDraft = template.status === 'DRAFT';

                  return (
                    <div
                      key={template.id}
                      className='group relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer'
                    >
                      <div className='aspect-[4/3] bg-gray-100 relative overflow-hidden'>
                        {template.thumbnail ? (
                          <img
                            src={template.thumbnail}
                            alt={template.name}
                            className='w-full h-full object-cover'
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className='w-full h-full bg-gray-100 flex items-center justify-center'>
                            <span className='text-gray-400 text-sm'>
                              No preview
                            </span>
                          </div>
                        )}

                        <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center opacity-0 group-hover:opacity-100'>
                          <div className='flex gap-3 transform translate-y-4 group-hover:translate-y-0 transition-transform'>
                            {/* ⭐ View button for non-draft; Edit for draft */}
                            {isDraft ? (
                              <button
                                onClick={(e) => handleEditClick(e, template)}
                                className='px-4 py-2 bg-white text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-100 shadow flex items-center gap-2'
                              >
                                <FiEdit2Component size={16} /> Edit
                              </button>
                            ) : (
                              <button
                                onClick={(e) => handleViewClick(e, template)}
                                className='px-4 py-2 bg-white text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-100 shadow flex items-center gap-2'
                              >
                                <FiEyeComponent size={16} /> View
                              </button>
                            )}

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteConfirm(template.id, template.name);
                              }}
                              disabled={deletingId === template.id}
                              className='px-4 py-2 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 shadow flex items-center gap-2 disabled:opacity-70'
                            >
                              {deletingId === template.id ? (
                                <>
                                  <svg
                                    className='animate-spin h-4 w-4'
                                    viewBox='0 0 24 24'
                                  >
                                    <circle
                                      cx='12'
                                      cy='12'
                                      r='10'
                                      stroke='currentColor'
                                      strokeWidth='4'
                                      fill='none'
                                    />
                                    <path
                                      fill='currentColor'
                                      d='M4 12a8 8 0 018-8v8z'
                                    />
                                  </svg>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <FiTrash2Component size={16} /> Delete
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className='p-4'>
                        <div className='flex justify-between items-start'>
                          <h3
                            className='font-semibold text-gray-800 truncate'
                            onMouseEnter={(e) =>
                              handleNameMouseEnter(e, template.name)
                            }
                            onMouseLeave={handleNameMouseLeave}
                          >
                            {template.name}
                          </h3>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ml-2 flex-shrink-0 ${template.status === 'DRAFT'
                              ? 'bg-yellow-100 text-yellow-800'
                              : template.status === 'UNDER_REVIEW'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                              }`}
                          >
                            {template.status.replace('_', ' ')}
                          </span>
                        </div>
                        <p className='text-xs text-gray-500 mt-1'>
                          {getTemplateCategory(template, allTemplates)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION */}
              {isCategoryFilterActive ? (
                displayTotalPages > 1 && (
                  <div className='flex items-center justify-between mt-8 text-sm text-gray-500'>
                    <span>
                      Showing {startItem} to {endItem} of {displayTotalCount}{' '}
                      templates
                    </span>

                    <div className='flex items-center justify-center gap-2'>
                      <button
                        onClick={() =>
                          handleLocalPageChange(displayCurrentPage - 1)
                        }
                        disabled={displayCurrentPage === 1}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        Previous
                      </button>

                      {(() => {
                        const pages: (number | string)[] = [];
                        const total = displayTotalPages;
                        const current = displayCurrentPage;
                        const delta = 1;

                        pages.push(1);

                        let rangeStart = Math.max(2, current - delta);
                        let rangeEnd = Math.min(total - 1, current + delta);

                        if (rangeStart > 2) pages.push('...');
                        for (let i = rangeStart; i <= rangeEnd; i++)
                          pages.push(i);
                        if (rangeEnd < total - 1) pages.push('...');
                        if (total > 1) pages.push(total);

                        return pages.map((page, index) => {
                          if (page === '...') {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className='px-3 py-2 text-sm text-gray-500'
                              >
                                …
                              </span>
                            );
                          }

                          const pageNumber = page as number;
                          const isActive = pageNumber === current;

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handleLocalPageChange(pageNumber)}
                              className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                                ? 'bg-orange-500 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        });
                      })()}

                      <button
                        onClick={() =>
                          handleLocalPageChange(displayCurrentPage + 1)
                        }
                        disabled={displayCurrentPage === displayTotalPages}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <>
                  {displayTotalPages > 1 && (
                    <div className='mt-10 flex items-center justify-center gap-2'>
                      <button
                        onClick={() =>
                          handlePageChange(displayCurrentPage - 1)
                        }
                        disabled={displayCurrentPage === 1}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        Previous
                      </button>

                      {(() => {
                        const pages = [];
                        const total = displayTotalPages;
                        const current = displayCurrentPage;
                        const delta = 1;

                        pages.push(1);

                        let rangeStart = Math.max(2, current - delta);
                        let rangeEnd = Math.min(total - 1, current + delta);

                        if (rangeStart > 2) pages.push('...');
                        for (let i = rangeStart; i <= rangeEnd; i++)
                          pages.push(i);
                        if (rangeEnd < total - 1) pages.push('...');
                        if (total > 1) pages.push(total);

                        return pages.map((page, index) => {
                          if (page === '...') {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className='px-3 py-2 text-sm text-gray-500'
                              >
                                …
                              </span>
                            );
                          }

                          const pageNumber = page as number;
                          const isActive = pageNumber === current;

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                                ? 'bg-orange-500 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        });
                      })()}

                      <button
                        onClick={() =>
                          handlePageChange(displayCurrentPage + 1)
                        }
                        disabled={displayCurrentPage === displayTotalPages}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        Next
                      </button>
                    </div>
                  )}

                  <div className='mt-4 text-center text-sm text-gray-600'>
                    Showing {startItem} to {endItem} of {displayTotalCount}{' '}
                    templates
                  </div>
                </>
              )}
            </>
          ) : (
            <div className='text-center py-16'>
              <p className='text-gray-500 text-lg'>No templates found</p>
              <p className='text-gray-400 mt-2'>
                {searchQuery || selectedCategories.length > 0
                  ? 'Try adjusting your search or filters'
                  : "You haven't created any templates yet."}
              </p>
            </div>
          )}
        </div>
      </div>

      <CreateTemplateModal
        fromTab2={fromTab2}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />

      {/* View modal */}
      <ViewTemplateModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        templateName={viewTemplateName}
        previewHtml={previewHtml}
        previewLoading={previewLoading}
        onEdit={handleEditFromViewModal}
        showEditButton={
          !!viewTemplateId &&
          (templates.find((t) => t.id === viewTemplateId)?.status === 'DRAFT' ||
            allTemplates.find((t) => t.id === viewTemplateId)?.status ===
            'DRAFT')
        }
      />

      {deleteConfirmOpen && templateToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4'>
          <div className='bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-semibold text-gray-900'>
                Delete Template?
              </h3>
              <button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setTemplateToDelete(null);
                }}
                className='text-gray-400 hover:text-gray-600 transition'
              >
                <FiXComponent size={22} />
              </button>
            </div>

            <div className='mb-6'>
              <p className='text-gray-600'>
                Are you sure you want to delete this template? This action
                cannot be undone.
              </p>
            </div>

            <div className='flex gap-3 justify-end'>
              <button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setTemplateToDelete(null);
                }}
                className='px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition'
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className='px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition flex items-center gap-2'
              >
                <FiTrash2Component size={16} />
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tooltip portal */}
      {tooltip &&
        ReactDOM.createPortal(
          <div
            role='tooltip'
            style={{
              position: 'absolute',
              top: tooltip.top,
              left: tooltip.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
            className='bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-md shadow-lg max-w-xs break-words whitespace-normal'
          >
            {tooltip.text}
            <span
              style={{
                position: 'absolute',
                bottom: -4,
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: 8,
                height: 8,
                background: '#111827',
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default YourTemplates;